import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { baseURL, imageURL } from "../../../../../Services/api/baseURL";
import { useNavigate, useParams } from "react-router";
import {
  ArrowLeftCircle, TrendingUp, ShoppingBag,
  DollarSign, Package, Download, ChevronUp, ChevronDown
} from "react-feather";
import moment from "moment";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend
} from "recharts";
import Loader from "../../../../Loader/Loader";

const BRAND = "#d3178a";

const periodOptions = [
  { label: "Today", value: "day" },
  { label: "Week", value: "week" },
  { label: "Month", value: "month" },
  { label: "Year", value: "year" },
  { label: "Custom", value: "custom" },
];

const fmt = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD", maximumFractionDigits: 2,
  }).format(n ?? 0);


// ── Custom Tooltip ────────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-gray-900 rounded-xl px-4 py-3 shadow-xl">
      <p className="text-xs text-gray-400 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-sm font-semibold" style={{ color: p.color }}>
          {p.name}: {p.name === "Orders" ? p.value : fmt(p.value)}
        </p>
      ))}
    </div>
  );
};

// ── Summary Card ──────────────────────────────────────────────────────────────
const SummaryCard = ({ icon: Icon, label, value, sub, iconBg, iconColor }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex items-start gap-4 hover:-translate-y-0.5 transition-transform duration-150">
    <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>
      <Icon size={18} className={iconColor} />
    </div>
    <div className="min-w-0">
      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-0.5">{label}</p>
      <p className="text-xl font-bold text-gray-900 truncate">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

// ── Sort Icon ─────────────────────────────────────────────────────────────────
const SortIcon = ({ field, sortField, sortDir }) => {
  if (sortField !== field) return <span className="text-gray-300 ml-1 text-xs">↕</span>;
  return sortDir === "asc"
    ? <ChevronUp size={12} className="inline ml-1 text-[#d3178a]" />
    : <ChevronDown size={12} className="inline ml-1 text-[#d3178a]" />;
};

// ── Main Component ────────────────────────────────────────────────────────────
const StoreRevenue = () => {
  const { id: storeId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [storeName, setStoreName] = useState("");
  const [period, setPeriod] = useState("month");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [subCategoryId, setSubCategoryId] = useState("");

  const [summary, setSummary] = useState({});
  const [chartData, setChartData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);

  const [sortField, setSortField] = useState("revenue");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const limit = 10;

  const fillChartData = (data, period) => {
    const map = {};
    data.forEach(d => { map[d.label] = d; });

    let labels = [];

    if (period === "day") {
      // 00–23 hours
      labels = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
    } else if (period === "week") {
      // 1–7 (Mon–Sun)
      labels = ["1", "2", "3", "4", "5", "6", "7"];
    } else if (period === "month") {
      // 01–31 days of current month
      const daysInMonth = moment().daysInMonth();
      labels = Array.from({ length: daysInMonth }, (_, i) =>
        String(i + 1).padStart(2, "0")
      );
    } else if (period === "year") {
      // 01–12 months
      labels = Array.from({ length: 12 }, (_, i) =>
        String(i + 1).padStart(2, "0")
      );
    } else if (period === "custom" && startDate && endDate) {
      // Fill every day between start and end
      const start = moment(startDate);
      const end = moment(endDate);
      while (start.isSameOrBefore(end, "day")) {
        labels.push(start.format("DD"));
        start.add(1, "day");
      }
    }

    return labels.map(label => ({
      label,
      revenue: map[label]?.revenue || 0,
      orders: map[label]?.orders || 0,
    }));
  };

  // ── Load dropdowns ──────────────────────────────────────────────────────────
  useEffect(() => {
    const token = JSON.parse(localStorage.getItem("token"));
    axios
      .get(`${baseURL}/api/admin/get-collections?page=1&limit=1000`, { headers: { Authorization: token } })
      .then(r => setCategories((r.data.data || []).filter(c => c.status === "active")));
    axios
      .get(`${baseURL}/api/admin/get-sub-collections?page=1&limit=1000`, { headers: { Authorization: token } })
      .then(r => setSubCategories((r.data.data || []).filter(c => c.status === "active")));
  }, []);

  // ── Build query params ──────────────────────────────────────────────────────
  const buildParams = useCallback(() => {
    const params = { period };
    if (period === "custom" && startDate && endDate) {
      params.start_date = moment(startDate).startOf("day").toISOString();
      params.end_date = moment(endDate).endOf("day").toISOString();
    }
    if (categoryId) params.category_id = categoryId;
    if (subCategoryId) params.sub_category_id = subCategoryId;
    return params;
  }, [period, startDate, endDate, categoryId, subCategoryId]);

  // ── Fetch revenue ───────────────────────────────────────────────────────────
  const fetchRevenue = useCallback(async () => {
    if (period === "custom" && (!startDate || !endDate)) return;
    setLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem("token"));
      const resp = await axios.get(
        `${baseURL}/api/store/get-store-revenue/${storeId}`,
        { params: buildParams(), headers: { Authorization: token } }
      );
      const d = resp.data.data;
      setSummary(d.summary || {});
      setChartData(d.chart || []);
      setTableData(d.products || []);
      setStoreName(d.storeName || "");
      setPage(1);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [storeId, buildParams, period, startDate, endDate]);

  useEffect(() => { fetchRevenue(); }, [fetchRevenue]);

  // ── Sorting ─────────────────────────────────────────────────────────────────
  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("desc"); }
  };

  // ── Dedup + Sorting ─────────────────────────────────────────────────────────
  const uniqueTable = Object.values(
    tableData.reduce((acc, row) => {
      const key = row._id || row.productName;
      if (!acc[key]) {
        acc[key] = { ...row };
      } else {
        acc[key].orders  = (acc[key].orders  ?? 0) + (row.orders  ?? 0);
        acc[key].qtySold = (acc[key].qtySold ?? 0) + (row.qtySold ?? 0);
        acc[key].revenue = (acc[key].revenue ?? 0) + (row.revenue ?? 0);
      }
      return acc;
    }, {})
  );

  const sortedTable = [...uniqueTable].sort((a, b) => {
    const va = a[sortField] ?? 0, vb = b[sortField] ?? 0;
    return sortDir === "asc" ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
  });
  const paginatedTable = sortedTable.slice((page - 1) * limit, page * limit);
  const totalPages = Math.ceil(sortedTable.length / limit);

  // ── CSV Export ──────────────────────────────────────────────────────────────
  const exportCSV = () => {
    const headers = ["Product", "Category", "Subcategory", "Orders", "Qty Sold", "Revenue"];
    const rows = sortedTable.map(r => [
      r.productName, r.categoryName, r.subCategoryName, r.orders, r.qtySold, r.revenue,
    ]);
    const csv = [headers, ...rows]
      .map(row => row.map(c => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${storeName}_revenue_${moment().format("YYYY-MM-DD")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ────────────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 p-6">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 bg-white border border-gray-200 rounded-full flex items-center justify-center text-[#d3178a] hover:bg-pink-50 hover:border-[#d3178a] transition-all"
          >
            <ArrowLeftCircle size={18} />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900 leading-tight">Revenue Analytics</h1>
            {storeName && <p className="text-sm text-gray-400 mt-0.5">{storeName}</p>}
          </div>
        </div>

        <button
          onClick={exportCSV}
          disabled={!tableData.length}
          className="flex items-center gap-2 bg-[#d3178a] hover:bg-[#a5116c] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
        >
          <Download size={14} />
          Export CSV
        </button>
      </div>

      {/* ── Filters ────────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-3">
        {/* Period toggle */}
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit mb-4">
          {periodOptions.map(o => (
            <button
              key={o.value}
              onClick={() => { setPeriod(o.value); setStartDate(""); setEndDate(""); }}
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all whitespace-nowrap
                ${period === o.value
                  ? "bg-[#d3178a] text-white shadow-sm"
                  : "text-gray-500 hover:text-[#d3178a]"}`}
            >
              {o.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Custom date range */}
          {period === "custom" && (
            <div className="flex items-center bg-gray-50 border border-gray-200 rounded-xl overflow-hidden focus-within:border-[#d3178a] transition-colors">
              {/* Start date */}
              <div className="flex items-center gap-2 px-3 py-2">
                <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <input
                  type="date"
                  value={startDate}
                  max={endDate || new Date().toISOString().split("T")[0]}
                  onChange={e => setStartDate(e.target.value)}
                  className="bg-transparent text-sm text-gray-700 outline-none w-32 cursor-pointer"
                />
              </div>

              {/* Divider */}
              <div className="flex items-center gap-1 px-2 border-x border-gray-200">
                <div className="w-3 h-px bg-gray-400" />
                <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>

              {/* End date */}
              <div className="flex items-center gap-2 px-3 py-2">
                <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <input
                  type="date"
                  value={endDate}
                  min={startDate}
                  max={new Date().toISOString().split("T")[0]}
                  onChange={e => setEndDate(e.target.value)}
                  className="bg-transparent text-sm text-gray-700 outline-none w-32 cursor-pointer"
                />
              </div>
            </div>
          )}


          {/* Category */}
          <select
            value={categoryId}
            onChange={e => { setCategoryId(e.target.value); setSubCategoryId(""); }}
            className="h-9 border border-gray-200 rounded-lg px-3 text-sm text-gray-700 bg-gray-50 focus:outline-none focus:border-[#d3178a] min-w-[160px]"
          >
            <option value="">All Categories</option>
            {categories.map(c => <option key={c._id} value={c._id}>{c.collection_name}</option>)}
          </select>

          {/* Subcategory */}
          <select
            value={subCategoryId}
            onChange={e => setSubCategoryId(e.target.value)}
            className="h-9 border border-gray-200 rounded-lg px-3 text-sm text-gray-700 bg-gray-50 focus:outline-none focus:border-[#d3178a] min-w-[160px]"
          >
            <option value="">All Subcategories</option>
            {(categoryId
              ? subCategories.filter(s => s.collection_id?._id === categoryId)
              : subCategories
            ).map(s => <option key={s._id} value={s._id}>{s.sub_collection_name}</option>)}
          </select>

          {(categoryId || subCategoryId) && (
            <button
              onClick={() => { setCategoryId(""); setSubCategoryId(""); }}
              className="h-9 px-3 text-sm text-gray-500 border border-gray-200 rounded-lg hover:border-[#d3178a] hover:text-[#d3178a] transition-colors"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader /></div>
      ) : (
        <>
          {/* ── Summary Cards ─────────────────────────────────────────────── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-x-4 mb-3">
            <SummaryCard
              icon={DollarSign} label="Total Revenue"
              value={fmt(summary.totalRevenue)}
              sub={`${summary.totalOrders ?? 0} orders`}
              iconBg="bg-pink-50" iconColor="text-[#d3178a]"
            />
            <SummaryCard
              icon={ShoppingBag} label="Total Orders"
              value={summary.totalOrders ?? 0}
              sub={`Avg ${fmt(summary.avgOrderValue)} / order`}
              iconBg="bg-violet-50" iconColor="text-violet-600"
            />
            <SummaryCard
              icon={Package} label="Items Sold"
              value={summary.totalQtySold ?? 0}
              sub={`${summary.uniqueProducts ?? 0} unique products`}
              iconBg="bg-sky-50" iconColor="text-sky-500"
            />
            <SummaryCard
              icon={TrendingUp} label="Avg Daily Rev"
              value={fmt(summary.avgDailyRevenue)}
              sub={summary.periodLabel ?? ""}
              iconBg="bg-emerald-50" iconColor="text-emerald-600"
            />
          </div>

          {/* ── Product Table ──────────────────────────────────────────────── */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-3">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-gray-900">Product Breakdown</h2>
              <span className="text-xs font-medium text-gray-400 bg-gray-100 px-3 py-1 rounded-full">
                {uniqueTable.length} products
              </span>
            </div>

            {paginatedTable.length > 0 ? (
              <>
                <div className="overflow-x-auto rounded-xl border border-gray-100">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-100">
                        {[
                          { label: "#", field: null, align: "text-left w-8" },
                          { label: "Product", field: null, align: "text-left" },
                          { label: "Category", field: null, align: "text-left" },
                          { label: "Subcategory", field: null, align: "text-left" },
                          { label: "Orders", field: "orders", align: "text-center" },
                          { label: "Qty Sold", field: "qtySold", align: "text-center" },
                          { label: "Revenue", field: "revenue", align: "text-right" },
                          { label: "% of Total", field: null, align: "text-left min-w-[120px]" },
                        ].map(({ label, field, align }) => (
                          <th
                            key={label}
                            onClick={() => field && handleSort(field)}
                            className={`px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase tracking-wider ${align}
                              ${field ? "cursor-pointer select-none hover:text-[#d3178a]" : ""}`}
                          >
                            {label}
                            {field && <SortIcon field={field} sortField={sortField} sortDir={sortDir} />}
                          </th>
                        ))}
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-50">
                      {paginatedTable.map((row, i) => {
                        const share = summary.totalRevenue
                          ? ((row.revenue / summary.totalRevenue) * 100).toFixed(1)
                          : 0;
                        return (
                          <tr key={row._id || i} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-3 text-gray-400 text-xs">{(page - 1) * limit + i + 1}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2.5">
                                {row.productImage && (
                                  <img
                                    src={`${imageURL}${row.productImage}`}
                                    alt=""
                                    className="w-8 h-8 rounded-lg object-cover border border-gray-100 flex-shrink-0"
                                  />
                                )}
                                <span className="font-medium text-gray-800">{row.productName || "—"}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-gray-600">{row.categoryName || "—"}</td>
                            <td className="px-4 py-3 text-gray-600">{row.subCategoryName || "—"}</td>
                            <td className="px-4 py-3 text-center text-gray-700">{row.orders ?? 0}</td>
                            <td className="px-4 py-3 text-center text-gray-700">{row.qtySold ?? 0}</td>
                            <td className="px-4 py-3 text-right font-semibold text-[#d3178a]">{fmt(row.revenue)}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                  <div
                                    className="h-full rounded-full bg-[#d3178a] opacity-70 transition-all duration-300"
                                    style={{ width: `${share}%` }}
                                  />
                                </div>
                                <span className="text-xs text-gray-500 w-10 text-right">{share}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>

                    <tfoot>
                      <tr className="bg-gray-50 border-t-2 border-gray-200 font-bold">
                        <td className="px-4 py-3 text-gray-500 text-xs" colSpan={4}>Total</td>
                        <td className="px-4 py-3 text-center text-gray-800">{summary.totalOrders ?? 0}</td>
                        <td className="px-4 py-3 text-center text-gray-800">{summary.totalQtySold ?? 0}</td>
                        <td className="px-4 py-3 text-right text-[#d3178a]">{fmt(summary.totalRevenue)}</td>
                        <td className="px-4 py-3 text-xs text-gray-500">100%</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-1.5 mt-5">
                    {/* Prev */}
                    <button
                      disabled={page === 1}
                      onClick={() => setPage(p => p - 1)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 text-sm disabled:opacity-30 hover:border-[#d3178a] hover:text-[#d3178a] transition-colors"
                    >‹</button>

                    {/* Smart page numbers */}
                    {(() => {
                      const pages = [];
                      const delta = 2; // pages around current

                      // Always show first page
                      pages.push(1);

                      // Left ellipsis
                      if (page - delta > 2) pages.push("...");

                      // Pages around current
                      for (let i = Math.max(2, page - delta); i <= Math.min(totalPages - 1, page + delta); i++) {
                        pages.push(i);
                      }

                      // Right ellipsis
                      if (page + delta < totalPages - 1) pages.push("...");

                      // Always show last page
                      if (totalPages > 1) pages.push(totalPages);

                      return pages.map((p, i) =>
                        p === "..." ? (
                          <span key={`ellipsis-${i}`} className="w-8 h-8 flex items-center justify-center text-gray-400 text-sm">
                            …
                          </span>
                        ) : (
                          <button
                            key={p}
                            onClick={() => setPage(p)}
                            className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-colors
              ${page === p
                                ? "bg-[#d3178a] text-white border border-[#d3178a]"
                                : "border border-gray-200 text-gray-600 hover:border-[#d3178a] hover:text-[#d3178a]"
                              }`}
                          >{p}</button>
                        )
                      );
                    })()}

                    {/* Next */}
                    <button
                      disabled={page === totalPages}
                      onClick={() => setPage(p => p + 1)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 text-sm disabled:opacity-30 hover:border-[#d3178a] hover:text-[#d3178a] transition-colors"
                    >›</button>

                    {/* Page info */}
                    <span className="text-xs text-gray-400 ml-2">
                      Page {page} of {totalPages}
                    </span>
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-400">
                <DollarSign size={40} className="text-gray-200" />
                <p className="text-sm">No revenue data found for the selected filters.</p>
              </div>
            )}
          </div>
          {/* ── Chart ─────────────────────────────────────────────────────── */}

          {chartData.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-3">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-base font-semibold text-gray-900">Revenue Over Time</h2>
                <span className="bg-pink-50 text-[#d3178a] text-sm font-semibold px-3 py-1 rounded-full">
                  {fmt(summary.totalRevenue)}
                </span>
              </div>
              <ResponsiveContainer width="100%" height={260}>
                {/* <BarChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}> */}
                <BarChart data={fillChartData(chartData, period)}>

                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false}
                    tickFormatter={v => v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v}`}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="revenue" name="Revenue" fill={BRAND} radius={[4, 4, 0, 0]} maxBarSize={44} />
                  {/* <Bar dataKey="orders"  name="Orders"  fill="#f9a8d4"  radius={[4,4,0,0]} maxBarSize={44} /> */}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default StoreRevenue;